/** A client the owner confirmed. `aliases` are other ways it's written (e.g. "ABC Corp"). */
export interface IClient {
  name: string;
  short: string;
  aliases?: string[];
  /** Law: the matter or file number (also the short name used in file names). */
  number?: string;
  /** Law: the practice area folder, e.g. Real Estate. */
  area?: string;
  /** Law: the responsible lawyer, as written in the matter list. */
  lawyer?: string;
}

const ABBREVIATIONS: [RegExp, string][] = [
  [/\bcorporation\b/gi, 'Corp'],
  [/\bincorporated\b/gi, 'Inc'],
  [/\blimited\b/gi, 'Ltd'],
  [/\bcompany\b/gi, 'Co'],
  [/\b(and|&)\b/gi, ' ']
];

/**
 * Legal endings ignored when comparing names ("ABC Corp." = "ABC Corporation" = "ABC"). Words like
 * Group or Holdings are NOT here: they can tell two businesses apart, so such near-matches go to review.
 */
const LEGAL: RegExp = /\b(corp|corporation|inc|incorporated|ltd|limited|llc|llp|co|company|ltée|ltee|enr)\b\.?/gi;

/** ABC Corporation → ABCCorp; Maple Ridge Dental → MapleRidgeDental. */
export function suggestShortName(name: string): string {
  let s: string = name;
  for (const [re, to] of ABBREVIATIONS) {
    s = s.replace(re, to);
  }
  return s
    .split(/[^A-Za-z0-9]+/)
    .filter(w => w)
    .map(w => (/^[a-z]/.test(w) ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join('')
    .slice(0, 24);
}

/** Lower case, accents and punctuation removed, legal endings dropped. */
export function normalizeName(name: string): string {
  return name
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(LEGAL, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function levenshtein(a: string, b: string): number {
  const dp: number[] = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    let prev: number = dp[0];
    dp[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const tmp: number = dp[j];
      dp[j] = Math.min(dp[j] + 1, dp[j - 1] + 1, prev + (a[i - 1] === b[j - 1] ? 0 : 1));
      prev = tmp;
    }
  }
  return dp[b.length];
}

export interface IClientMatch {
  client: IClient;
  /** 0–1: how sure the text names this client. */
  score: number;
}

/**
 * The confirmed client a written name refers to, if any. Exact (ignoring legal endings) is
 * certain; containment ("Maple Ridge Dental Group" ~ "Maple Ridge Dental") and near-spellings
 * score lower so they fall below the auto-file threshold and go to review.
 */
/** "24-0153", "24/0153", "240153" → "240153": letters and digits only, upper case. */
export function normalizeNumber(n: string): string {
  return n.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/**
 * The matter a number written in a document refers to: "File No. 24-0153", "Our File: 24/0153",
 * "Matter 24-0153" all find matter 24-0153. Only whole numbers match, never part of a longer one.
 */
export function matchNumber(written: string | undefined, clients: IClient[]): IClient | undefined {
  const text: string = written ?? '';
  // Also with spaces between digit groups closed up ("24 0153" → "240153").
  const tokens: string[] = [text, text.replace(/(\d)\s+(?=\d)/g, '$1')]
    .flatMap(t => t.match(/[A-Za-z]{0,4}[-\s]?\d[\w./-]*\d|\d+/g) ?? [])
    // "File 23-0417" is also tried as "23-0417": words before a number aren't part of it.
    .flatMap(t => [t, t.replace(/^[A-Za-z]+[-\s]+(?=\d)/, '')]);
  for (const t of tokens) {
    const n: string = normalizeNumber(t);
    const hit: IClient | undefined = clients.filter(c => c.number && normalizeNumber(c.number) === n)[0];
    if (hit) {
      return hit;
    }
  }
  return undefined;
}

export function matchClient(written: string | undefined, clients: IClient[]): IClientMatch | undefined {
  // Law: a matter number is exact, whatever words surround it.
  const byNumber: IClient | undefined = matchNumber(written, clients);
  if (byNumber) {
    return { client: byNumber, score: 1 };
  }
  const w: string = normalizeName(written ?? '');
  if (!w) {
    return undefined;
  }
  let best: IClientMatch | undefined;
  const consider = (client: IClient, score: number): void => {
    if (!best || score > best.score) {
      best = { client, score };
    }
  };
  // A person's name written "Last, First" on the list is often "FIRST LAST" or "LAST FIRST" on a
  // document (passports, IRCC letters): the same words in another order are near-certain, unless
  // two people on the list share those words.
  const wt: string[] = w.split(' ');
  const sameWords = (client: IClient): boolean => [client.name, ...(client.aliases ?? [])]
    .some(name => name.indexOf(',') !== -1 && wt.length > 1 && normalizeName(name).split(' ').sort().join(' ') === [...wt].sort().join(' '));
  const reordered: IClient[] = clients.filter(sameWords);
  for (const client of clients) {
    const names: string[] = [client.name, client.short, ...(client.aliases ?? [])].map(normalizeName).filter(n => n);
    for (const n of names) {
      if (n === w || n.replace(/ /g, '') === w.replace(/ /g, '')) {
        consider(client, 1);
        continue;
      }
      if (reordered.length === 1 && reordered[0] === client) {
        consider(client, 0.95);
        continue;
      }
      const nt: string[] = n.split(' ');
      const nInW: boolean = nt.every(t => wt.includes(t));
      const wInN: boolean = wt.every(t => nt.includes(t));
      if (nInW || wInN) {
        // More tokens in common relative to the longer name = more certain.
        const overlap: number = Math.min(wt.length, nt.length) / Math.max(wt.length, nt.length);
        consider(client, 0.6 + 0.2 * overlap);
        continue;
      }
      const distance: number = levenshtein(w, n);
      const ratio: number = 1 - distance / Math.max(w.length, n.length);
      if (ratio >= 0.85) {
        consider(client, 0.7 * ratio);
      }
    }
  }
  return best;
}
